import React from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { BankSelect } from "../BankAccounts";
import { DialogTitle } from "../ui/dialog";

export const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
export const label = "block text-sm font-medium mb-1.5";
export const methods = ["cash", "bank", "upi", "qr"];
export const pill = (active) => `border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`;

export const cleanSplits = (rows) => rows.filter((r) => parseFloat(r.amount) > 0)
  .map((r) => ({ method: r.method, amount: Math.round(parseFloat(r.amount) * 100) / 100, reference: r.reference || "", bank_account: r.bank_account || "" }));
export const splitsTotal = (splits) => Math.round(splits.reduce((s, r) => s + r.amount, 0) * 100) / 100;

export async function downloadDocPdf(id, number, size = "a4", action = "download") {
  try {
    const res = await api.get(`/vouchers/${id}/pdf`, { params: { size }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (action === "print") {
      const w = window.open(url, "_blank");
      if (w) {
        w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) { console.warn(e); } });
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else {
        window.URL.revokeObjectURL(url);
        toast.error("Allow pop-ups to print");
      }
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}${size === "thermal" ? "-80mm" : ""}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  } catch {
    toast.error(action === "print" ? "Could not print PDF" : "Could not download PDF");
  }
}

const isPartialRows = (rows) => rows.length === 2 && rows[0].method === "cash" && rows[1].method !== "cash";
const r2s = (rows) => Math.round(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) * 100) / 100;

function PartialSwitch({ rows, setRows, prefix, target }) {
  const partial = isPartialRows(rows);
  const sum = r2s(rows);
  const full = () => setRows([{ method: rows[0]?.method || "cash", amount: target ? String(target) : (sum ? String(sum) : ""), reference: rows[0]?.reference || "", bank_account: rows[0]?.bank_account || "" }]);
  const part = () => {
    if (partial) return;
    const cashAmt = rows.filter((r) => r.method === "cash").reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const online = rows.find((r) => r.method !== "cash") || { method: "bank", reference: "" };
    const due = target || sum;
    const cash = cashAmt ? String(Math.round(cashAmt * 100) / 100) : "";
    const rest = due ? String(Math.max(Math.round((due - cashAmt) * 100) / 100, 0)) : "";
    setRows([{ method: "cash", amount: cash, reference: "" }, { ...online, amount: rest }]);
  };
  return (
    <div className="grid grid-cols-2 gap-2 mb-2">
      <button type="button" data-testid={`${prefix}-pay-full`} onClick={full} className={`h-9 rounded-md text-xs ${pill(!partial)}`}>Single method</button>
      <button type="button" data-testid={`${prefix}-pay-partial`} onClick={part} className={`h-9 rounded-md text-xs ${pill(partial)}`}>Partial: Cash + Bank/QR/UPI</button>
    </div>
  );
}

const SplitRow = ({ r, i, prefix, partial, canRemove, set, rm }) => (
  <div data-testid={`${prefix}-split-${i}`} className="rounded-md border border-border p-2.5 space-y-2">
    <div className="flex items-center gap-2">
      <div className="grid grid-cols-4 gap-1 flex-1">
        {methods.filter((m) => !partial || (i === 0 ? m === "cash" : m !== "cash")).map((m) => (
          <button key={m} type="button" data-testid={`${prefix}-split-${i}-${m}`} onClick={() => set(i, { method: m })}
            className={`h-9 rounded-md text-xs uppercase ${pill(r.method === m)}`}>{m}</button>
        ))}
      </div>
      {canRemove && (
        <button type="button" data-testid={`${prefix}-split-remove-${i}`} onClick={() => rm(i)} className="shrink-0 h-9 w-9 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"><X size={15} /></button>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2">
      <input data-testid={`${prefix}-split-amount-${i}`} type="number" step="0.01" min="0" className={field} placeholder="Amount (₹)"
        value={r.amount} onChange={(e) => set(i, { amount: e.target.value })} />
      <input data-testid={`${prefix}-split-ref-${i}`} className={field} placeholder={r.method === "cash" ? "No reference" : "Reference / UTR"}
        disabled={r.method === "cash"} value={r.reference} onChange={(e) => set(i, { reference: e.target.value })} />
    </div>
    {r.method !== "cash" && <BankSelect testid={`${prefix}-split-bank-${i}`} value={r.bank_account} onChange={(v) => set(i, { bank_account: v })} />}
  </div>
);

export function SplitRows({ rows, setRows, prefix, partialOption, target }) {
  const partial = partialOption && isPartialRows(rows);
  const set = (i, patch) => {
    let next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    if (partial && target && i === 0 && patch.amount !== undefined) {
      next = next.map((r, idx) => (idx === 1 ? { ...r, amount: String(Math.max(Math.round((target - (parseFloat(patch.amount) || 0)) * 100) / 100, 0)) } : r));
    }
    setRows(next);
  };
  const add = () => setRows([...rows, { method: "bank", amount: "", reference: "" }]);
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const mismatch = target && Math.abs(total - target) > 0.009;
  return (
    <div>
      <label className={label}>Payment {rows.length > 1 ? "— split across methods" : "method & amount"}</label>
      {partialOption && <PartialSwitch rows={rows} setRows={setRows} prefix={prefix} target={target} />}
      <div className="space-y-2">
        {rows.map((r, i) => <SplitRow key={i} r={r} i={i} prefix={prefix} partial={partial} canRemove={rows.length > 1 && !partial} set={set} rm={rm} />)}
      </div>
      <div className="flex items-center justify-between mt-2">
        {partial ? <span data-testid={`${prefix}-partial-formula`} className="text-xs text-muted-foreground num">Cash ₹{(parseFloat(rows[0].amount) || 0).toFixed(2)} + {rows[1].method.toUpperCase()} ₹{(parseFloat(rows[1].amount) || 0).toFixed(2)}</span>
          : <button type="button" data-testid={`${prefix}-split-add`} onClick={add} className="h-8 px-3 rounded-md border border-dashed border-input text-xs flex items-center gap-1.5 text-muted-foreground hover:bg-muted transition-colors"><Plus size={13} /> Add method</button>}
        <span className="text-sm text-muted-foreground">Total <span data-testid={`${prefix}-split-total`} className="num font-bold text-foreground">₹{total.toFixed(2)}</span></span>
      </div>
      {mismatch ? <p data-testid={`${prefix}-target-note`} className="text-xs text-amber-600 mt-1">Invoice balance is ₹{target.toFixed(2)}. {total < target ? `₹${(target - total).toFixed(2)} will stay pending.` : `₹${(total - target).toFixed(2)} extra will be kept on account.`}</p> : null}
    </div>
  );
}

export const DocTitle = ({ title, preview }) => (
  <DialogTitle className="font-display text-2xl flex items-center justify-between gap-3">
    <span className="truncate">{title}</span>
    <span className="num text-xs px-2 py-1 rounded bg-accent/15 text-accent truncate max-w-[45%] shrink-0" title={preview}>{preview}</span>
  </DialogTitle>
);

export const TypeChooser = ({ items, value, onPick, testidOf, round, newValue, setNewValue, onAdd, addTestid, newTestid, placeholder, extra }) => (
  <>
    <div className="flex flex-wrap gap-2">
      {items.map((t) => (
        <button key={t} type="button" data-testid={testidOf(t)} onClick={() => onPick(t)}
          className={`${round ? "h-9 rounded-full" : "h-10 rounded-md"} px-3 text-sm ${pill(value === t)}`}>{t}</button>
      ))}
      {extra}
    </div>
    <div className="flex gap-2 mt-2">
      <input data-testid={newTestid} className={field + " h-9"} placeholder={placeholder} value={newValue}
        onChange={(e) => setNewValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())} />
      <button type="button" data-testid={addTestid} onClick={onAdd} className="h-9 px-3 rounded-md border border-input hover:bg-muted"><Plus size={15} /></button>
    </div>
  </>
);
